import { useEffect, useRef, useState } from 'react';
import { Calendar, History } from 'lucide-react';
import type { PeriodPreset } from '@contracts';
import { resolvePeriod } from '@/lib/period';
import { fmtDay } from '@/shared/formatters';
import { useProcurementFilters } from '@/features/procurement/procurementContext';

/**
 * Periodo de CREACIÓN de la orden, con el mismo patrón visual que el
 * DateFilter de ventas (chip + botón calendario + popover de presets), más la
 * opción clave del dominio: "Histórico" (sin filtro de fechas), el default del
 * tablero operativo.
 */
const QUICK: { value: PeriodPreset; label: string }[] = [
  { value: 'today', label: 'Hoy' },
  { value: 'this_week', label: 'Semana' },
  { value: 'this_month', label: 'Este mes' },
  { value: 'last_month', label: 'Mes ant.' },
  { value: 'last_30d', label: '30 días' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'ytd', label: 'Este año' },
];

const LABEL: Record<string, string> = Object.fromEntries(QUICK.map((q) => [q.value, q.label]));

export function ProcurementDateFilter() {
  const { period, setPeriod } = useProcurementFilters();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const isHistoric = period == null;
  const isPreset = typeof period === 'string';
  const triggerLabel = isHistoric ? 'Histórico' : isPreset ? (LABEL[period] ?? 'Período') : 'Personalizado';
  const range = period ? resolvePeriod(period) : null;

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-1.5">
        <span className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600">
          {triggerLabel}
        </span>
        <button
          onClick={() => setOpen((o) => !o)}
          title="Período de creación"
          className="grid h-8 w-8 place-items-center rounded-lg bg-red-600 text-white hover:bg-red-700"
        >
          <Calendar className="h-4 w-4" />
        </button>
      </div>

      {open && (
        <div className="absolute left-0 z-40 mt-1.5 w-80 rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
          <div className="mb-3 flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-red-50 text-red-600">
              <Calendar className="h-4 w-4" />
            </span>
            <h3 className="text-sm font-bold text-slate-800">Fecha de creación</h3>
          </div>

          {/* Histórico primero: es el modo operativo (ver TODO lo abierto). */}
          <button
            onClick={() => setPeriod(null)}
            className={`mb-2 flex w-full items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition ${
              isHistoric ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <History className="h-3.5 w-3.5" /> Histórico (todas las órdenes)
          </button>

          <div className="grid grid-cols-4 gap-1.5">
            {QUICK.map((q) => {
              const active = isPreset && period === q.value;
              return (
                <button
                  key={q.value}
                  onClick={() => setPeriod(q.value)}
                  className={`rounded-lg px-2 py-1.5 text-xs font-semibold transition ${
                    active ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {q.label}
                </button>
              );
            })}
          </div>

          <div className="my-3 rounded-xl bg-red-50 px-3 py-2 text-center">
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Rango aplicado</p>
            <p className="mt-0.5 text-xs font-semibold text-red-700">
              {range ? (
                <>
                  {fmtDay(range.from)} <span className="text-slate-400">→</span> {fmtDay(range.to)}
                </>
              ) : (
                'Todas las órdenes, sin acotar por fecha'
              )}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-400">Desde</label>
              <input
                type="date"
                value={range?.from ?? ''}
                max={range?.to}
                onChange={(e) => e.target.value && setPeriod({ from: e.target.value, to: range?.to ?? e.target.value })}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-red-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-400">Hasta</label>
              <input
                type="date"
                value={range?.to ?? ''}
                min={range?.from}
                onChange={(e) => e.target.value && setPeriod({ from: range?.from ?? e.target.value, to: e.target.value })}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-red-500"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
